import { useState, useCallback, useEffect, useRef } from 'react';
import { ChevronUp, ChevronDown, HelpCircle } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { Button } from '@reflex/ui/components/ui/button';
import { Textarea } from '@reflex/ui/components/ui/textarea';
import { Kbd } from '@reflex/ui/components/shared/Kbd';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import {
  clearAskUserQuestionDraft,
  loadAskUserQuestionDraft,
  saveAskUserQuestionDraft,
} from './ask-user-question-draft.js';
import type { AskUserQuestionDraft } from './ask-user-question-draft.js';
import { useStepHotkeys } from './use-step-hotkeys.js';

export interface AskUserQuestionItem {
  header?: string;
  question: string;
  multiSelect?: boolean;
  options: Array<{ label: string; description?: string; preview?: string }>;
}

export interface AskUserQuestionAnnotation {
  preview?: string;
  notes?: string;
}

export interface AskUserQuestionCardProps {
  questions: AskUserQuestionItem[];
  onSubmit: (
    answers: Record<string, string>,
    annotations: Record<string, AskUserQuestionAnnotation>,
  ) => void;
  onSkip: () => void;
  onDismiss: () => void;
  isPending: boolean;
  /**
   * Stable per-prompt key (e.g. `<agentId>:<requestId>`). When set,
   * in-progress answers are persisted so remounts (switching sessions and
   * back) restore them. Omit to keep state purely local.
   */
  draftKey?: string;
  /**
   * Start the card collapsed. Only applies to the inline (no-overlay) render
   * path — e.g. viewing a completed session in an isolated renderer where the
   * prompt was never answered. A live prompt lifted into the docked overlay is
   * always expanded so the user can act on it immediately.
   */
  initiallyCollapsed?: boolean;
}

const OPTION_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// The global keydown handler intentionally ignores textareas/inputs, so the
// textarea's local handler must share the same predicate to stay consistent.
const isPlainEnter = (e: KeyboardEvent | React.KeyboardEvent): boolean =>
  e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey;

interface RestoredDraftState {
  questionIdx: number;
  selections: Map<number, Set<number>>;
  notes: Map<number, string>;
}

/**
 * Rebuild card state from a persisted draft, dropping anything that no longer
 * fits the current questions (out-of-range indices, extra selections on a
 * single-select question) so a stale draft can never render an invalid state.
 */
function restoreDraftState(
  draft: AskUserQuestionDraft,
  questions: AskUserQuestionItem[],
): RestoredDraftState {
  const selections = new Map<number, Set<number>>();
  const notes = new Map<number, string>();
  for (const [key, optIndices] of Object.entries(draft.selections)) {
    const qi = Number(key);
    const question = questions[qi];
    if (!question) continue;
    const valid = optIndices.filter((oi) => oi < question.options.length);
    const kept = question.multiSelect ? valid : valid.slice(0, 1);
    if (kept.length > 0) selections.set(qi, new Set(kept));
  }
  for (const [key, note] of Object.entries(draft.notes)) {
    const qi = Number(key);
    if (questions[qi] && note.length > 0) notes.set(qi, note);
  }
  return {
    questionIdx: Math.min(Math.max(0, draft.questionIdx), questions.length - 1),
    selections,
    notes,
  };
}

export function AskUserQuestionCard({
  questions,
  onSubmit,
  onSkip,
  onDismiss,
  isPending,
  draftKey,
  initiallyCollapsed,
}: AskUserQuestionCardProps) {
  const [restoredDraft] = useState(() => {
    const draft = draftKey ? loadAskUserQuestionDraft(draftKey) : null;
    return draft ? restoreDraftState(draft, questions) : null;
  });
  const [questionIdx, setQuestionIdx] = useState(restoredDraft?.questionIdx ?? 0);
  const [collapsed, setCollapsed] = useState(initiallyCollapsed ?? false);
  const [selections, setSelections] = useState<Map<number, Set<number>>>(
    () => restoredDraft?.selections ?? new Map(),
  );
  const [notesByQuestion, setNotesByQuestion] = useState<Map<number, string>>(
    () => restoredDraft?.notes ?? new Map(),
  );
  const [focusByQuestion, setFocusByQuestion] = useState<Map<number, number>>(() => new Map());
  const cardRef = useRef<HTMLDivElement>(null);
  const regionRef = useRef<HTMLDivElement>(null);
  // True while the options region holds more content below its visible fold.
  // Drives a fade shadow at the bottom of the region so a tall option list
  // (the last item clipped at the fold) signals that it scrolls, instead of
  // looking like the card simply ran out of options.
  const [canScrollDown, setCanScrollDown] = useState(false);

  useEffect(() => {
    if (!draftKey) return;
    const hasSelections = [...selections.values()].some((set) => set.size > 0);
    const hasNotes = [...notesByQuestion.values()].some((note) => note.length > 0);
    if (!hasSelections && !hasNotes && questionIdx === 0) {
      clearAskUserQuestionDraft(draftKey);
      return;
    }
    saveAskUserQuestionDraft(draftKey, {
      questionIdx,
      selections: Object.fromEntries([...selections].map(([qi, set]) => [String(qi), [...set]])),
      notes: Object.fromEntries([...notesByQuestion].map(([qi, note]) => [String(qi), note])),
    });
  }, [draftKey, questionIdx, selections, notesByQuestion]);

  // Track whether the options region can scroll further down. Re-runs when the
  // question changes (the content swaps), when the card collapses/expands (the
  // region unmounts/remounts, so the listeners re-bind), and observes resize so
  // a window relayout re-evaluates the fold. The region contains only options,
  // so this is a cheap single-element check.
  useEffect(() => {
    const el = regionRef.current;
    if (!el) return;
    const update = () => {
      const more = el.scrollHeight - el.scrollTop - el.clientHeight > 1;
      setCanScrollDown((prev) => (prev === more ? prev : more));
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [questionIdx, questions, collapsed]);

  const totalQuestions = questions.length;
  const question = questions[questionIdx];
  const currentSelection = selections.get(questionIdx) ?? new Set<number>();
  const hasPreviews = (question?.options ?? []).some((o) => o.preview);
  // Widen the whole card if any question carries previews, so the width stays
  // stable while paging between questions rather than jumping per-question.
  const anyPreviews = questions.some((q) => q.options.some((o) => o.preview));
  const focusedOptIdx = focusByQuestion.get(questionIdx) ?? [...currentSelection][0] ?? 0;
  const focusedPreview = question?.options[focusedOptIdx]?.preview;
  const currentNotes = notesByQuestion.get(questionIdx) ?? '';

  const toggleOption = useCallback(
    (optIdx: number) => {
      if (!question) return;
      setSelections((prev) => {
        const next = new Map(prev);
        const set = new Set(next.get(questionIdx) ?? []);
        if (question.multiSelect) {
          if (set.has(optIdx)) set.delete(optIdx);
          else set.add(optIdx);
        } else {
          set.clear();
          set.add(optIdx);
        }
        next.set(questionIdx, set);
        return next;
      });
      setFocusByQuestion((prev) => new Map(prev).set(questionIdx, optIdx));
    },
    [question, questionIdx],
  );

  const handleContinue = useCallback(() => {
    if (isPending || !question || currentSelection.size === 0) return;

    if (questionIdx < totalQuestions - 1) {
      setQuestionIdx((i) => i + 1);
      return;
    }

    const answers: Record<string, string> = {};
    const annotations: Record<string, AskUserQuestionAnnotation> = {};
    for (let qi = 0; qi < questions.length; qi++) {
      const q = questions[qi];
      const sel = selections.get(qi) ?? new Set<number>();
      const selected = [...sel].map((oi) => q.options[oi]).filter(Boolean);
      const labels = selected.map((o) => o.label);
      if (labels.length > 0) answers[q.question] = labels.join(', ');

      const previews = selected.map((o) => o.preview).filter((p): p is string => Boolean(p));
      const notes = (notesByQuestion.get(qi) ?? '').trim();
      if (previews.length > 0 || notes) {
        annotations[q.question] = {
          ...(previews.length > 0 ? { preview: previews.join('\n\n') } : {}),
          ...(notes ? { notes } : {}),
        };
      }
    }
    onSubmit(answers, annotations);
  }, [
    currentSelection.size,
    isPending,
    notesByQuestion,
    onSubmit,
    question,
    questionIdx,
    questions,
    selections,
    totalQuestions,
  ]);

  const handleSkip = useCallback(() => {
    onSkip();
  }, [onSkip]);

  const handleDismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  const handleNotesKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!isPending && isPlainEnter(e)) {
        e.preventDefault();
        handleContinue();
      }
    },
    [isPending, handleContinue],
  );

  const goToPreviousQuestion = useCallback(() => setQuestionIdx((i) => Math.max(0, i - 1)), []);
  const goToNextQuestion = useCallback(
    () => setQuestionIdx((i) => Math.min(totalQuestions - 1, i + 1)),
    [totalQuestions],
  );

  // The same `alt+↑/↓` that steps through turns in the transcript pages through
  // the questions here. While this prompt is up it owns those keys — the
  // transcript's stepper stands down behind it.
  useStepHotkeys({
    enabled: !collapsed && totalQuestions > 1,
    onPrevious: goToPreviousQuestion,
    onNext: goToNextQuestion,
  });

  useEffect(() => {
    // While collapsed the bar is inert: only the focusable chevron stays
    // interactive (its native Enter/Space expands it). Gate the global
    // shortcuts off so a stray Escape can't dismiss the preserved prompt and
    // stray letter/Enter keys can't mutate or submit an unseen selection.
    if (collapsed) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.isComposing || e.repeat) return;
      const target = e.target as HTMLElement;

      // Escape is handled ahead of the input guard, otherwise it dies whenever
      // focus sits in the disabled composer, whose own capture handler would
      // swallow the key before a bubble listener ever heard it.
      if (e.key === 'Escape') {
        // An open Radix layer (dialog, dropdown, popover) owns Escape; closing
        // the prompt underneath it would interrupt the agent's turn instead of
        // closing the layer the user is looking at.
        if (
          target.closest?.(
            '[role="dialog"], [role="alertdialog"], [data-radix-popper-content-wrapper]',
          )
        )
          return;
        const isTextField =
          target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
        if (isTextField) {
          // Inside the card's own notes field the first Escape only steps out
          // of the field, so a reflexive Esc while typing can't interrupt the
          // turn; the next one dismisses. A live text field elsewhere (say the
          // queued-message editor docked above this card) keeps its own Escape
          // semantics untouched. The disabled composer is not editable, so it
          // never matches this carve-out and Escape dismisses from there.
          if (cardRef.current?.contains(target)) {
            e.preventDefault();
            e.stopPropagation();
            target.blur();
          }
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        handleDismiss();
        return;
      }

      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
        return;

      const letter = e.key.toUpperCase();
      const optIdx = OPTION_LETTERS.indexOf(letter);
      if (optIdx >= 0 && question && optIdx < question.options.length && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        toggleOption(optIdx);
        return;
      }
      if (isPlainEnter(e)) {
        e.preventDefault();
        handleContinue();
      }
    };
    // Capture phase, so Escape wins over focused widgets (the composer editor
    // consumes it to stop the agent) while a prompt is the active surface.
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [collapsed, handleContinue, handleDismiss, question, toggleOption]);

  if (!question) return null;

  const isLastQuestion = questionIdx === totalQuestions - 1;

  return (
    <div
      ref={cardRef}
      className={cn(
        // A flex column with min-h-0, so a height-constrained host (the docked
        // overlay slot) pins the header and footer while only the middle
        // section scrolls. Unconstrained hosts render at natural height.
        'mx-auto my-3 flex min-h-0 w-full flex-col overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm',
        anyPreviews ? 'max-w-4xl' : 'max-w-2xl',
      )}
      data-testid="ask-user-question-card"
    >
      <div
        className={cn(
          'flex shrink-0 items-center gap-2 px-3 py-2',
          !collapsed && 'border-b border-border/40',
        )}
      >
        <HelpCircle className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground">
          {question.header ?? 'Questions'}
          {collapsed && (
            <span className="ml-2 font-normal text-muted-foreground/70">{question.question}</span>
          )}
        </span>
        {totalQuestions > 1 && (
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={goToPreviousQuestion}
              disabled={questionIdx === 0}
              className="size-auto p-0 hover:bg-transparent hover:text-muted-foreground disabled:opacity-30 dark:hover:bg-transparent"
              aria-label="Previous question"
              title="Previous question (alt+↑)"
            >
              <ChevronUp className="size-3" />
            </Button>
            <span className="tabular-nums">
              {questionIdx + 1} of {totalQuestions}
            </span>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={goToNextQuestion}
              disabled={questionIdx === totalQuestions - 1}
              className="size-auto p-0 hover:bg-transparent hover:text-muted-foreground disabled:opacity-30 dark:hover:bg-transparent"
              aria-label="Next question"
              title="Next question (alt+↓)"
            >
              <ChevronDown className="size-3" />
            </Button>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => setCollapsed((c) => !c)}
          className="h-5 w-5 shrink-0 rounded-full border border-border bg-muted text-foreground/70 hover:bg-accent hover:text-foreground"
          aria-label={collapsed ? 'Expand prompt' : 'Minimize prompt'}
          aria-expanded={!collapsed}
        >
          {collapsed ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </Button>
      </div>

      {!collapsed && (
        <>
          <div className="relative flex min-h-0 flex-1 flex-col">
            <div
              ref={regionRef}
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
              data-testid="ask-user-question-scroll-region"
            >
              <div className="px-3 pb-1 pt-3">
                <p className="text-sm text-foreground/90">
                  {totalQuestions > 1 && (
                    <span className="font-semibold">{questionIdx + 1}.&nbsp;</span>
                  )}
                  {question.question}
                </p>
              </div>

              <div className="flex flex-col gap-3 px-3 pb-3 pt-1 sm:flex-row">
                <div className={cn('flex min-w-0 flex-col', hasPreviews ? 'sm:w-2/5' : 'w-full')}>
                  {question.options.map((opt, oi) => {
                    const isSelected = currentSelection.has(oi);
                    const isFocused = hasPreviews && oi === focusedOptIdx;
                    const letter = OPTION_LETTERS[oi];
                    return (
                      <button
                        key={oi}
                        type="button"
                        onClick={() => toggleOption(oi)}
                        onMouseEnter={
                          hasPreviews
                            ? () => setFocusByQuestion((prev) => new Map(prev).set(questionIdx, oi))
                            : undefined
                        }
                        className={cn(
                          'flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                          'focus:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                          isSelected
                            ? 'bg-accent text-accent-foreground'
                            : isFocused
                              ? 'bg-accent/50 text-foreground/80'
                              : 'text-foreground/80 hover:bg-accent/50',
                        )}
                        aria-pressed={isSelected}
                      >
                        <span
                          className={cn(
                            'flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] font-semibold leading-none transition-colors',
                            isSelected
                              ? 'border-foreground bg-foreground text-background'
                              : 'border-border/70 text-muted-foreground',
                          )}
                        >
                          {letter}
                        </span>
                        <div className="min-w-0">
                          <span className="block text-xs font-medium leading-4">{opt.label}</span>
                          {opt.description && (
                            <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                              {opt.description}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {hasPreviews && (
                  <div className="min-w-0 w-full sm:w-3/5" data-testid="ask-user-question-preview">
                    {focusedPreview ? (
                      <pre className="h-full max-h-40 overflow-auto whitespace-pre rounded-lg border border-border/50 bg-muted/40 p-2.5 font-mono text-[11px] leading-relaxed text-foreground/80 sm:max-h-64">
                        {focusedPreview}
                      </pre>
                    ) : (
                      <div className="flex h-full min-h-16 items-center justify-center rounded-lg border border-dashed border-border/50 p-2.5 text-[11px] italic text-muted-foreground">
                        No preview
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Fade shadow at the region's bottom fold signals the option list
                keeps going below when it scrolls — otherwise the last visible
                option reads as "all of them" and the fold hides the rest. */}
            {canScrollDown && (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-card to-transparent"
              />
            )}
          </div>

          {/* Notes + footer form one always-visible action panel below the
              scroll region. Keeping the notes out of the scrolling options area
              means the textarea stays reachable (and writable) no matter how many
              options fill the region above — previously it sat at the bottom of
              the scroll content and got scrolled out of reach. */}
          <div className="shrink-0 border-t border-border/40 px-3 py-2">
            <div className="pb-2">
              <label
                htmlFor={`ask-user-notes-${questionIdx}`}
                className="mb-1 block text-[11px] font-medium text-muted-foreground"
              >
                Notes (optional)
              </label>
              <Textarea
                id={`ask-user-notes-${questionIdx}`}
                value={currentNotes}
                onChange={(e) =>
                  setNotesByQuestion((prev) => new Map(prev).set(questionIdx, e.target.value))
                }
                onKeyDown={handleNotesKeyDown}
                rows={2}
                placeholder="Add context for your selection…"
                className="min-h-0 resize-y rounded-lg border-border/60 bg-background px-2.5 py-1.5 text-xs text-foreground/90 md:text-xs dark:bg-background"
              />
            </div>
            <div className="flex items-center gap-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={handleDismiss}
                    disabled={isPending}
                    className="h-auto gap-1.5 p-0 font-normal text-muted-foreground hover:bg-transparent hover:text-muted-foreground dark:hover:bg-transparent"
                  >
                    Dismiss
                    <Kbd size="xs">Esc</Kbd>
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-56">
                  Close without answering and interrupt the agent's turn. Pressing Esc does the
                  same.
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={handleSkip}
                    disabled={isPending}
                    className="h-auto p-0 font-normal text-muted-foreground hover:bg-transparent hover:text-muted-foreground dark:hover:bg-transparent"
                  >
                    Skip
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-56">
                  Send no answer. The agent keeps going and uses its best judgment.
                </TooltipContent>
              </Tooltip>
              <Button
                size="sm"
                onClick={handleContinue}
                disabled={currentSelection.size === 0 || isPending}
                className="ml-auto h-7 gap-1.5 text-xs"
              >
                {isLastQuestion ? 'Continue' : 'Next'}
                <Kbd size="xs" className="border-primary-foreground/30 text-primary-foreground/70">
                  ↵
                </Kbd>
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
